<template>
  <v-container>
    <v-container class="pt-0">
      <!-- Buttons panel for expanded size -->
      <v-row>
        <v-col class="text-right hidden-sm-and-down">
          <v-btn
            class="mr-4"
            color="grey darken-1"
            :loading="reloadLoading"
            v-if="savingEnable"
            @click="reload"
            outlined
            tile
          >
            <v-icon left>mdi-autorenew</v-icon>
            {{ $t("reload") }}
          </v-btn>
          <v-btn
            color="primaryLigth"
            :loading="saveLoading"
            :disabled="saveDisabled"
            v-if="savingEnable"
            @click="save"
            outlined
            tile
          >
            <v-icon left>mdi-content-save-outline</v-icon>
            {{ $t("save") }}
          </v-btn>
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12" md="6">
          <v-text-field
            v-model="aetitle"
            :loading="firstLoading"
            :class="{'disable-events': !savingEnable}"
            @input="saveDisabled=false;"
            :label="$t('AE Title')"
            dense
          ></v-text-field>
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12" md="6">
          <v-text-field
            v-model="port"
            :loading="firstLoading"
            :class="{'disable-events': !savingEnable, 'hint-color': true}"
            :hint="$t('Service restart required.')"
            @input="saveDisabled=false;"
            :label="$t('Port')"
            type="number"
            persistent-hint
            dense
          ></v-text-field>
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12" md="6" class="pb-0">
          <v-text-field
            v-model="pdusize"
            :loading="firstLoading"
            :class="{'disable-events': !savingEnable}"
            @input="saveDisabled=false;"
            :label="$t('Max PDU Data Length')"
            type="number"
            dense
          ></v-text-field>
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12" md="6">
          <v-select
            v-model="charset"
            :items="charsetItems"
            :loading="firstLoading"
            :class="{'disable-events': !savingEnable}"
            @change="saveDisabled=false;"
            :label="$t('Character Set')"
            dense
          ></v-select>
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12" md="6">
          <v-subheader class="pl-0">{{ $t("DICOM Clients") }}</v-subheader>
        </v-col>
        <v-col cols="12" md="6" class="text-right">
          <v-btn color="primaryLigth" @click="addClientModal" v-if="savingEnable" outlined tile>
            <v-icon left>mdi-plus</v-icon>{{ $t("add client") }}
          </v-btn>
        </v-col>
      </v-row>
    </v-container>

    <v-data-table
      :headers="clientsHeader"
      :items="clients"
      :items-per-page="15"
      @click:row="updateClientModal"
      class="mb-4"
      :footer-props="{ 
        itemsPerPageText: $t('Clients per page:'),
        itemsPerPageAllText: $t('All'),
      }"
    >
      <!-- Port 0 => N/A -->
      <template v-slot:[`item.port`]="{ item }">
        {{ item.port != 0 ? item.port : 'N/A' }}
      </template>

      <!-- Send checkbox -->
      <template v-slot:[`item.send`]="{ item }">
        <v-checkbox
          v-model="item.send"
          :disabled="!savingEnable"
          v-on:click.stop="checkboxClick(item, 'send')"
          inset
        ></v-checkbox>
      </template>
      
      <!-- Archive checkbox -->
      <template v-slot:[`item.archive`]="{ item }">
        <v-checkbox
          v-model="item.archive"
          :disabled="!savingEnable"
          v-on:click.stop="checkboxClick(item, 'archive')"
          inset
        ></v-checkbox>
      </template>

      <!-- Connection check -->
      <template v-slot:[`item.check`]="{ item }">
        <template v-if="item.port != 0">
          <div class="text-center d-flex align-center justify-space-around">
            <v-tooltip right open-delay="500" close-delay="500">
              <template v-slot:activator="{ on, attrs }">
                <v-btn icon v-on:click.stop="runCheck(item)" v-if="!item.loading" v-bind="attrs" v-on="on">
                  <v-icon color="blue" v-if="item.check === null">mdi-sync-circle</v-icon>
                  <v-icon color="green" v-else-if="item.check === true">mdi-check-circle</v-icon>
                  <v-icon color="red" v-else-if="item.check === false">mdi-alert-circle</v-icon>
                </v-btn>
                <v-btn icon v-else loading></v-btn>
              </template>
              <span>C-ECHO</span>
            </v-tooltip>
          </div>
        </template>
      </template>
    </v-data-table>

    <!-- Buttons panel for shrinked size -->
    <v-container class="text-right hidden-md-and-up px-6 mb-6">
      <v-btn
        class="mr-4"
        color="grey darken-1"
        :loading="reloadLoading"
        @click="reload"
        outlined
        tile
      >
        <v-icon left>mdi-autorenew</v-icon>{{ $t("reload") }}
      </v-btn>

      <v-btn
        color="primaryLigth"
        :loading="saveLoading"
        :disabled="saveDisabled"
        @click="save"
        outlined
        tile
      >
        <v-icon left>mdi-content-save-outline</v-icon>{{ $t("save") }}
      </v-btn>
    </v-container>

    <DicomClientModal
      ref="dicomClientModal"
      @new="addClient"
      @update="updateClient"
      @remove="removeClient"
    ></DicomClientModal>
  </v-container>
</template>

<script>
import axios from "axios";
import i18n from "../../i18n";
import DicomClientModal from "./DicomClientModal";
import Charset from "../../lists/charset";
import EventBus from "../../event-bus";

export default {
  components: {
    DicomClientModal,
  },
  data: () => ({
    firstLoading: true,
    saveLoading: false,
    saveDisabled: true,
    reloadLoading: false,
    aetitle: null,
    port: 0,
    pdusize: 0,
    clientsHeader: [
      { text: i18n.t("Name"), value: "name" },
      { text: i18n.t("AE Title"), value: "aeTitle" },
      { text: i18n.t("IP Address"), value: "ip" },
      { text: i18n.t("Port"), value: "port" },
      { text: i18n.t("Send"), width: "100px", value: "send" },
      { text: i18n.t("Archive"), width: "100px", value: "archive" },
      { text: "", width: "100px", value: "check" },
    ],
    clients: [],
    clientSearch: null,
    charset: "Default",
    charsetItems: Charset,
    counter: 0,
    savingEnable: false,
  }),
  mounted() {
    // Permissions
    let configuration = this.$store.getters.resources.configuration;
    
    if (configuration.dicom.update) {
        this.savingEnable = true;
    }

    this.loadData();
  },
  methods: {
    sendError(error) {
      if (error.response) {
        if (
          error.response.status == 500 &&
          error.response.data.status == "error"
        ) {
          EventBus.$emit("SHOW_ERROR", error.response.data.error);
        } else {
          EventBus.$emit("SHOW_ERROR", error.response.data);
        }
      } else {
        EventBus.$emit("SHOW_ERROR", error);
      }
    },
    runCheck(item) {
      // Start loading
      item.loading = true;
      // Test DICOM node
      axios({
        url: "/api/v1/config/dicom/check",
        method: "POST",
        data: {
          ip: item.ip,
          port: item.port,
          aeTitle: item.aeTitle,
        }
      })
        .then((resp) => {
          // Stop loading
          item.loading = false;
          // Process response
          if (
            !resp.data ||
            !resp.data.status ||
            !(resp.data.status === "done")
          ) {
            item.check = false;
            this.$store.dispatch("logError", {
              title: resp.data.error,
              message: resp.data.message,
            });
          } else {
            item.check = true;
          }
        })
        .catch((error) => {
          item.loading = false;
          item.check = null;
          this.sendError(error);
        });
    },
    loadData() {
      axios({
        url: "/api/v1/config/dicom",
        method: "GET",
      })
        .then((resp) => {
          if (
            !resp.data ||
            !resp.data.status ||
            !(resp.data.status == "done")
          ) {
            this.sendError(resp.data.error);
          } else {
            // Load DICOM data
            this.aetitle = resp.data.dicom.aeTitle;
            this.port = resp.data.dicom.port;
            this.pdusize = resp.data.dicom.maxPduSize;
            this.charset = resp.data.dicom.charset;
            // Add ID
            var clients = resp.data.dicom.clients;
            var counter = 0;
            clients.forEach(function (client) {
              client.id = counter++;
              client.check = null;
              client.loading = false;
            });
            this.clients = clients;
            this.counter = counter;
            // Stop loadings
            this.reloadLoading = false;
            this.firstLoading = false;
            this.saveDisabled = true;
          }
        })
        .catch((error) => {
          this.reloadLoading = false;
          this.firstLoading = false;
          this.sendError(error);
        });
    },
    addClientModal() {
      this.$refs.dicomClientModal.show(null, this.counter);
      this.counter = this.counter + 1;
    },
    updateClientModal(data) {
      this.$refs.dicomClientModal.show(data);
    },
    addClient(data) {
      this.saveDisabled = false;
      this.clients.push(data);
    },
    updateClient(data) {
      this.saveDisabled = false;
      this.clients.forEach(function (client) {
        if (client.id == data.id) {
          client.name = data.name;
          client.aeTitle = data.aeTitle;
          client.ip = data.ip;
          client.port = data.port;
          client.preferredTransferSyntaxes = data.preferredTransferSyntaxes;
        }
      });
    },
    removeClient(data) {
      this.saveDisabled = false;
      this.clients = this.clients.filter(function (el) {
        return el.id != data.id;
      });
    },
    checkboxClick(item, value) {
      this.saveDisabled = false;
      let itemIndex = this.clients.indexOf(item);

      let editItem;
      if (value == 'send') {
        if (item.send) {
          editItem = { ...this.clients[itemIndex], ...{ send: true } };
        } else {
          editItem = { ...this.clients[itemIndex], ...{ send: false } };
        }
      } else {
        if (item.archive) {
          editItem = { ...this.clients[itemIndex], ...{ archive: true } };
        } else {
          editItem = { ...this.clients[itemIndex], ...{ archive: false } };
        }
      }
      
      this.clients.splice(itemIndex, 1, editItem);
    },
    reload() {
      this.reloadLoading = true;
      this.loadData();
    },
    save() {
      this.saveLoading = true;

      // Send configuration
      axios({
        url: "/api/v1/config/dicom",
        method: "PUT",
        data: {
          aeTitle: this.aetitle,
          port: Number(this.port),
          maxPduSize: Number(this.pdusize),
          charset: this.charset,
          clients: this.clients,
        },
      })
        .then((response) => {
          this.saveLoading = false;
          if (
            !response.data ||
            !response.data.status ||
            !(response.data.status == "done")
          ) {
            this.sendError(response.data.error);
          } else {
            this.saveDisabled = true;
          }
        })
        .catch((error) => {
          this.saveLoading = false;
          this.sendError(error);
        });
    },
    checkNested(obj, level, ...rest) {
      if (obj === undefined) return false;
      if (rest.length == 0 && {}.hasOwnProperty.call(obj, level)) return true;
      return this.checkNested(obj[level], ...rest);
    },
  },
};
</script>

<style scoped>
.disable-events {
  pointer-events: none;
}
.hint-color >>> .v-text-field__details .v-messages {
  color: red
}
</style>